import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Input, message, Spin, Table } from 'antd';
import { connect } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { SearchOutlined } from '@ant-design/icons';
import { clearData, getUniteRoomDetail, submitUniteRoom } from '@/redux/actions/order';
import { IroomInfo } from '@/redux/reducers/order';
import IconFont from '@/components/IconFont';
import { HistoryParams } from '@/utils/type';
import { renderIntlLabel } from '@/utils';
import CheckboxCollapseItem from './CheckboxCollapseItem';
import styles from './index.module.scss';

const UniteRoom = ({
  afterOpenChange,
  myRef,
  allRoomTypeMap,
  joinRoomNoMap,
  roomIdMap,
  getUniteRoomDetailFn,
  submitUniteRoomFn,
  fnClearData,
  uniteRoomDetailLoading,
  submitUniteRoomLoading,
}) => {
  const { resvNo } = useParams<HistoryParams>();
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedKeysMap, setSelectedKeysMap] = useState<{ [key: string]: Array<number> }>({});
  const [selectedRowsMap, setSelectedRowsMap] = useState<{ [key: string]: Array<any> }>({});
  const [selectedMainResvNo, setSelectedMainResvNo] = useState('');

  const rowsRef = useRef(selectedRowsMap);

  const filterAllRoomTypeMap = useMemo(() => {
    if (searchText && allRoomTypeMap) {
      const obj = {};
      Object.keys(allRoomTypeMap)?.forEach((key) => {
        obj[key] = allRoomTypeMap[key]?.filter((item) => item.roomNo?.includes(searchText));
      });
      return obj;
    }
    return allRoomTypeMap || {};
  }, [searchText, allRoomTypeMap]);
  const dataSource: Array<IroomInfo> = useMemo(() => {
    let attr = [];
    if (selectedRowsMap) {
      Object.keys(selectedRowsMap)?.forEach((key) => {
        attr = attr.concat(selectedRowsMap[key]);
      });
    }
    // 按联房分小组，将房主排在每小组最前面；再按添加顺序排序
    attr.sort((a: IroomInfo, b: IroomInfo) => {
      if (a && a.joinRoomNo && b && b.joinRoomNo) {
        if (a.joinRoomNo === b.joinRoomNo) {
          if (a.roomMasterFlag && !b.roomMasterFlag) {
            return -1;
          }
          if (!a.roomMasterFlag && b.roomMasterFlag) {
            return 1;
          }
        }
      }
      if (a.updateTime < b.updateTime) {
        return -1;
      }
      if (a.updateTime > b.updateTime) {
        return 1;
      }
      return 0;
    });
    return attr;
  }, [selectedRowsMap]);

  const showSubmitBtn = useMemo(() => {
    if (!selectedMainResvNo) {
      return false;
    }
    return dataSource?.some((item) => item.resvNo !== selectedMainResvNo);
  }, [dataSource, selectedMainResvNo]);

  const onOpen = () => {
    setVisible(true);
  };

  const resetSelected = () => {
    setSelectedRowsMap({});
    setSelectedKeysMap({});
    setSelectedMainResvNo('');
    rowsRef.current = {};
    fnClearData({
      allRoomTypeMap: {},
      joinRoomNoMap: {},
      roomIdMap: {},
    });
  };

  const onClose = () => {
    setVisible(false);
    resetSelected();
  };

  const handleConfirm = debounce(() => {
    if (!selectedMainResvNo) {
      message.warning(renderIntlLabel('order_detail_msg_please_appoint_new_homeowner'));
      return;
    }
    const otherResList = [];
    dataSource.forEach((item) => {
      if (item.resvNo !== selectedMainResvNo && !otherResList.includes(item.resvNo)) {
        otherResList.push(item.resvNo);
      }
    });
    if (!otherResList.length) {
      message.warning(renderIntlLabel('order_detail_msg_at_least_two_orders_unite'));
      return;
    }
    submitUniteRoomFn({ toResvNo: selectedMainResvNo, fromResvNos: otherResList }).then(() => {
      setVisible(false);
      resetSelected();
      afterOpenChange?.(selectedMainResvNo);
    });
  }, 200);

  const handleCancel = () => {
    setVisible(false);
    resetSelected();
  };

  const onSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // 根据联房号将关联联房选中或者取消
  const updateUniteRoomSelected = (initSelectedKeysMap, isCancel, currentUniteRoomObj) => {
    const tempKeysMap = { ...initSelectedKeysMap };
    Object.keys(currentUniteRoomObj || {})?.forEach((key) => {
      let sourceRoomIdList = tempKeysMap[key] || [];
      const targetRoomIdList = currentUniteRoomObj[key] || [];
      if (isCancel) {
        sourceRoomIdList = sourceRoomIdList.filter((val) => !targetRoomIdList.includes(val));
      } else {
        sourceRoomIdList = Array.from(new Set(sourceRoomIdList.concat(targetRoomIdList)));
      }
      tempKeysMap[key] = sourceRoomIdList;
    });
    const rowsMap = {};
    Object.keys(tempKeysMap)?.forEach((key) => {
      const list = [];
      tempKeysMap[key].forEach((val) => {
        list.push(roomIdMap[val]);
      });
      rowsMap[key] = list;
    });
    return [tempKeysMap, rowsMap];
  };
  const getDifferentKeyList = (source, target) => {
    const targetKeyList = (target || []).map((roomId) => roomId);
    return source?.filter((roomId) => !targetKeyList?.includes(roomId));
  };

  // 记录点击的时间
  const updateRowsMap = (rowsMap) => {
    const obj = { ...rowsMap };
    // console.log('start,', JSON.stringify(obj, null, 4));
    Object.keys(obj)?.forEach((key) => {
      const preList = rowsRef.current[key] || [];
      obj[key] = obj[key]?.map((item) => {
        const temp = { ...item };
        if (
          !Array.isArray(preList) ||
          !preList.length ||
          !preList.find((sub) => sub.roomId === temp.roomId)
        ) {
          temp.updateTime = new Date().getTime();
        }
        return temp;
      });
    });
    // console.log('end', JSON.stringify(obj, null, 4));
    return obj;
  };

  // 拷贝之前记录的时间,避免被清空
  const copyRoomUpdateTime = (preRowsMap, roomTypeName, checkedRows) => {
    return [...checkedRows]?.map((item) => {
      const index = preRowsMap[roomTypeName]?.findIndex(
        (selectedItem) => selectedItem.roomId === item.roomId,
      );
      if (index !== -1) {
        // 追加更新时间
        return preRowsMap[roomTypeName][index];
      }
      return item;
    });
  };

  // 拷贝之前记录的时间
  const copyJoinRoomUpdateTime = (tempRowsMap, targetRowsMap) => {
    const result = {};
    Object.keys(targetRowsMap)?.forEach((name) => {
      result[name] = copyRoomUpdateTime(tempRowsMap, name, targetRowsMap[name]);
    });
    return result;
  };

  const handelSelectedChange = (roomTypeName, checkedKeys, checkedRows) => {
    const preKeyList = (selectedKeysMap[roomTypeName] || []).map((roomId) => roomId);
    const isCancel = checkedKeys?.every((roomId) => preKeyList.includes(roomId));
    const differentKeyList = getDifferentKeyList(
      isCancel ? selectedKeysMap[roomTypeName] : checkedKeys,
      isCancel ? checkedKeys : selectedKeysMap[roomTypeName],
    );

    let tempKeysMap = { ...selectedKeysMap, [roomTypeName]: checkedKeys };
    let tempRowsMap = {
      ...selectedRowsMap,
      [roomTypeName]: copyRoomUpdateTime(selectedRowsMap, roomTypeName, checkedRows),
    };
    // console.log('***差异化数据---是否需要联房***', JSON.stringify(differentKeyList));
    if (
      Array.isArray(differentKeyList) &&
      differentKeyList.length &&
      differentKeyList.some((roomId) => roomIdMap[roomId]?.joinRoomNo)
    ) {
      const obj = {};
      differentKeyList.forEach((roomId) => {
        const { joinRoomNo } = roomIdMap[roomId];
        // const uniteKey = mainStatus + joinRoomNo;
        if (!obj[joinRoomNo]) {
          // 选中或者取消所有的联房
          const attr = updateUniteRoomSelected(
            tempKeysMap, // 当前选择的
            isCancel,
            joinRoomNoMap[joinRoomNo], // 当前受影响联房的房型
          );
          tempKeysMap = { ...attr[0] };
          tempRowsMap = { ...copyJoinRoomUpdateTime(tempRowsMap, attr[1]) };
          obj[joinRoomNo] = true;
        }
      });
      tempRowsMap = updateRowsMap(tempRowsMap);
      rowsRef.current = tempRowsMap;
      setSelectedKeysMap(tempKeysMap);
      setSelectedRowsMap(tempRowsMap);
    } else {
      // console.log('非联房');
      tempRowsMap = updateRowsMap(tempRowsMap);
      rowsRef.current = tempRowsMap;
      setSelectedKeysMap(tempKeysMap);
      setSelectedRowsMap(tempRowsMap);
    }
  };
  const handleBecomeOwner = (value) => {
    setSelectedMainResvNo(value);
  };
  const handleDeleteRoom = (record) => {
    const { roomTypeName, roomId, joinRoomNo } = record;
    // 所删除是新房主，重置
    if (record.resvNo === selectedMainResvNo) {
      setSelectedMainResvNo('');
    }
    if (joinRoomNo) {
      const attr = updateUniteRoomSelected(selectedKeysMap, true, joinRoomNoMap[joinRoomNo]);
      const tempRowsMap = copyJoinRoomUpdateTime(selectedRowsMap, attr[1]);
      rowsRef.current = tempRowsMap;
      setSelectedKeysMap(attr[0]);
      setSelectedRowsMap(tempRowsMap);
    } else {
      const keyList = selectedKeysMap[roomTypeName]?.filter((val) => val !== roomId);
      const rowList = selectedRowsMap[roomTypeName]?.filter((item) => item.roomId !== roomId);
      const tempKeysMap = { ...selectedKeysMap, [roomTypeName]: keyList };
      const tempRowsMap = {
        ...selectedRowsMap,
        [roomTypeName]: copyRoomUpdateTime(selectedRowsMap, roomTypeName, rowList),
      };
      rowsRef.current = tempRowsMap;
      setSelectedKeysMap(tempKeysMap);
      setSelectedRowsMap(tempRowsMap);
    }
  };

  // 根据主单初始化已选房间
  useEffect(() => {
    if (resvNo && allRoomTypeMap) {
      const keyMap = {};
      let rowMap = {};
      Object.keys(allRoomTypeMap)?.forEach((roomTypeNamekey) => {
        const keyList = [];
        const rowList = [];
        allRoomTypeMap[roomTypeNamekey]?.forEach((item) => {
          if (resvNo === item.resvNo) {
            keyList.push(item.roomId);
            rowList.push(item);
          }
        });
        keyMap[roomTypeNamekey] = keyList;
        rowMap[roomTypeNamekey] = rowList;
      });
      rowMap = updateRowsMap(rowMap);
      rowsRef.current = rowMap;
      setSelectedKeysMap(keyMap);
      setSelectedRowsMap(rowMap);
    }
  }, [resvNo, allRoomTypeMap]);
  // 初始化新房主选中态
  useEffect(() => {
    if (Array.isArray(dataSource)) {
      let tempSource = [...dataSource];
      tempSource = tempSource.filter(
        (item) =>
          // mainStatus  1：预订，2：在住
          (item.mainStatus === 2 && !item.joinRoomNo) ||
          (item.mainStatus === 2 && item.joinRoomNo && item.roomMasterFlag),
      );
      // 第一次赋值
      if (!selectedMainResvNo) {
        let mainResvNo = '';
        if (Array.isArray(tempSource) && tempSource.length) {
          mainResvNo = tempSource[0].resvNo;
        }
        setSelectedMainResvNo(mainResvNo);
      } else if (tempSource.every((item) => item.resvNo !== selectedMainResvNo)) {
        // 勾选时将已选房主删除
        setSelectedMainResvNo('');
      }
    }
  }, [dataSource, selectedMainResvNo]);

  useEffect(() => {
    if (visible) {
      getUniteRoomDetailFn({ resvNo });
    }
  }, [visible]);

  useImperativeHandle(
    myRef,
    () => ({
      onClose,
      onOpen,
    }),
    [],
  );
  const columns: ColumnsType<IroomInfo> = [
    // {
    //   title: 'id',
    //   dataIndex: 'updateTime',
    // },
    {
      title: renderIntlLabel('order_roomorder_room_type'),
      dataIndex: 'roomTypeName',
      render: (val) => <div style={{ wordBreak: 'break-word' }}>{val}</div>,
    },
    {
      title: renderIntlLabel('order_roomorder_room_number'),
      dataIndex: 'roomNo',
      render: (_, record) => (
        <>
          <div className="flex-vcenter" style={{ wordBreak: 'break-word' }}>
            <div
              className={`${record.mainStatus === 1 ? styles.bookTag : ''} ${
                record.mainStatus === 2 ? styles.checkinTag : ''
              }`}
            >
              &nbsp;
            </div>
            <div className={styles.roomNo}>{record.roomNo}</div>
            {record.joinRoomNo ? <div className={styles.unite}>{record.joinRoomNo}</div> : null}
          </div>
        </>
      ),
    },
    {
      title: `${renderIntlLabel('order_roomorder_original_house_owner')}/${renderIntlLabel(
        'order_roomorder_subscriber',
      )}`,
      dataIndex: 'name',
      render: (val, record) => {
        let text = '';
        if (record.joinRoomNo) {
          const isAllReserve = dataSource
            .filter((item) => record.joinRoomNo === item.joinRoomNo)
            .every((item) => item.mainStatus === 1);
          // 是否纯预订
          if (isAllReserve) {
            text = renderIntlLabel('order_roomorder_subscriber');
          } else {
            text = renderIntlLabel('order_roomorder_house_owner');
          }
        } else if (record.mainStatus === 1) {
          text = renderIntlLabel('order_roomorder_subscriber');
        } else {
          text = renderIntlLabel('order_roomorder_house_owner');
        }
        return (
          <>
            <div className="flex-vcenter" style={{ wordBreak: 'break-word', whiteSpace: 'nowrap' }}>
              <div className={styles.newOwner} style={{ marginRight: '4px' }}>
                {text}
              </div>
              <div>{val}</div>
            </div>
          </>
        );
      },
    },
    {
      title: renderIntlLabel('order_detail_new_homeowner'),
      render: (_, record) => {
        const showBtn =
          (record.mainStatus === 2 && !record.joinRoomNo) ||
          (record.mainStatus === 2 && record.joinRoomNo && record.roomMasterFlag);
        const isSelected = record.resvNo === selectedMainResvNo;
        let nodes = null;
        if (showBtn && isSelected) {
          nodes = (
            <div className={styles.newOwner}>{renderIntlLabel('order_detail_new_homeowner')}</div>
          );
        } else if (showBtn && !isSelected) {
          nodes = (
            <>
              <div onClick={() => handleBecomeOwner(record.resvNo)}>
                <IconFont style={{ color: '#3F79FF', marginRight: '4px' }} type="icon-zhiding" />
                <Button type="link">{renderIntlLabel('order_action_set_house_owner')}</Button>
              </div>
            </>
          );
        } else {
          nodes = null;
        }
        return nodes;
      },
    },
    {
      title: renderIntlLabel('order_action'),
      align: 'right',
      render: (_, record) => (
        <>
          {!record.joinRoomNo || record.roomMasterFlag ? (
            <Button type="link" onClick={() => handleDeleteRoom(record)}>
              {renderIntlLabel('order_action_delete')}
            </Button>
          ) : null}
        </>
      ),
    },
  ];
  return (
    <>
      <Drawer
        className={styles.container}
        title={renderIntlLabel('order_action_unite_room')}
        placement="right"
        width="60%"
        footer={
          <>
            <Button
              type="primary"
              style={{
                backgroundColor: showSubmitBtn ? '' : '#f5f5f5',
                color: showSubmitBtn ? '' : 'rgba(0, 0, 0, 0.25)',
                borderColor: showSubmitBtn ? '' : '#d9d9d9',
              }}
              loading={submitUniteRoomLoading || uniteRoomDetailLoading}
              onClick={handleConfirm}
            >
              {renderIntlLabel('order_action_confirm_unite_room')}
            </Button>
            <Button onClick={handleCancel}>{renderIntlLabel('order_action_cancel')}</Button>
          </>
        }
        destroyOnClose
        maskClosable={false}
        visible={visible}
        onClose={onClose}
      >
        <div className={styles.uniteDrawerBody}>
          <div className="groupLabel" style={{ margin: '16px 0 24px' }}>
            {renderIntlLabel('order_detail_label_room_select')}
          </div>
          <div className="flex">
            <div className={styles.left}>
              <div className={styles.search}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#969799' }} />}
                  allowClear
                  placeholder={renderIntlLabel('order_detail_placeholder_search_roomtype_roomno')}
                  value={searchText}
                  onChange={onSearchChange}
                />
              </div>
              <Spin spinning={uniteRoomDetailLoading}>
                {Object.keys(filterAllRoomTypeMap)?.map((key) => (
                  <CheckboxCollapseItem
                    key={key}
                    roomTypeName={key}
                    disabledCheckAll={!!searchText}
                    recordList={filterAllRoomTypeMap[key]}
                    value={selectedKeysMap[key]}
                    onSelectedChange={handelSelectedChange}
                  />
                ))}
              </Spin>
            </div>
            <div className={styles.right}>
              <Table
                rowKey="roomId"
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.order,
  };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  getUniteRoomDetailFn: getUniteRoomDetail,
  submitUniteRoomFn: submitUniteRoom,
  fnClearData: clearData,
})(React.memo(UniteRoom));

export default React.forwardRef<any, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
